import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import api, { productImage } from "@/lib/api";
import { useCart } from "@/lib/cart";
import StorefrontLayout from "@/components/StorefrontLayout";

export default function Checkout() {
  const { items, add, clear, coupon, setCoupon } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({ customer_name: "", customer_email: "" });
  const [shipping, setShipping] = useState({
    name: "", address1: "", address2: "", city: "",
    state_code: "", zip: "", country_code: "US", phone: "",
  });
  const [products, setProducts] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [stripeReady, setStripeReady] = useState(true);
  const [quote, setQuote] = useState(null);
  const [bump, setBump] = useState(null);
  const [bumpAdded, setBumpAdded] = useState(false);

  // Load per-cart-item product records so we know which are physical (Printful)
  useEffect(() => {
    Promise.all(items.map((i) =>
      api.get(`/products/${i.product_id}`).then((r) => [i.product_id, r.data]).catch(() => [i.product_id, null]),
    )).then((pairs) => setProducts(Object.fromEntries(pairs)));
  }, [items]);

  const hasPhysical = items.some((i) => {
    const p = products[i.product_id];
    return p && (p.printful_sync_variant_id || ["Print", "Apparel", "Home", "Accessories"].includes(p.category));
  });

  useEffect(() => {
    api.get("/checkout/configured").then((r) => setStripeReady(!!r.data?.configured)).catch(() => setStripeReady(false));
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("status") === "cancelled") setErr("Payment was cancelled. You can try again.");
  }, []);

  // Fetch order bump suggestion
  useEffect(() => {
    if (items.length === 0) { setBump(null); return; }
    api.post("/checkout/order-bump", items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })))
      .then((r) => setBump(r.data?.bump || null))
      .catch(() => setBump(null));
  }, [items]);

  // Refresh quote when the cart or coupon changes. Name/email don't affect pricing.
  useEffect(() => {
    if (items.length === 0) { setQuote(null); return; }
    const body = {
      items: items.map((i) => ({
        product_id: i.product_id,
        quantity: i.quantity,
        custom_price: i.custom_price != null ? i.custom_price : undefined,
      })),
      customer_email: "quote@example.com",
      customer_name: "quote",
      origin_url: window.location.origin,
      coupon_code: coupon?.code || undefined,
    };
    api.post("/checkout/quote", body).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [items, coupon]);

  const addBump = () => {
    if (!bump) return;
    // add a synthetic product with a bump price override via custom_price? No — custom_price must be >= list.
    // Instead: apply the coupon-style bump via product add + a dedicated "MERIDIAN15" won't cover this.
    // Simplest: add the product at bump_price by fetching the product doc and using custom_price = bump_price
    // but PWYW requires >= list. Bump price is < list. We'll fake it by adding a companion "add-on" flag → but backend recomputes.
    // Pragmatic approach: add the bump product normally, then auto-apply MERIDIAN15 code (already stackable).
    api.get(`/products/${bump.product_id}`).then((r) => {
      add(r.data);
      setBumpAdded(true);
      // Kick a subtle 15% coupon if none applied — softens the "no discount" perception
      if (!coupon) {
        api.post("/coupons/validate", { code: "MERIDIAN15" })
          .then((rr) => rr.data && setCoupon(rr.data))
          .catch(() => {});
      }
    }).catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (hasPhysical) {
      const s = shipping;
      if (!s.address1 || !s.city || !s.zip || !s.country_code) {
        setErr("Shipping address required for physical items — fill in address, city, ZIP, country.");
        return;
      }
    }
    setBusy(true); setErr("");
    try {
      const shippingPayload = hasPhysical
        ? { ...shipping, name: shipping.name || form.customer_name }
        : null;
      if (stripeReady) {
        const r = await api.post("/checkout/session", {
          customer_email: form.customer_email,
          customer_name: form.customer_name,
          items: items.map((i) => ({
            product_id: i.product_id,
            quantity: i.quantity,
            custom_price: i.custom_price != null ? i.custom_price : undefined,
          })),
          origin_url: window.location.origin,
          coupon_code: coupon?.code || undefined,
          shipping: shippingPayload,
        });
        // NOTE: do NOT clear the cart here. OrderConfirmation clears once payment
        // is confirmed paid — so if the user cancels on Stripe, they return with
        // their items intact and can retry checkout in one click.
        window.location.href = r.data.url;
        return;
      }
      const r = await api.post("/orders", {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: shippingPayload,
      });
      clear();
      nav(`/order/${r.data.id}`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not start checkout.");
    } finally { setBusy(false); }
  };

  const totalDisplay = useMemo(() => {
    if (quote) return quote.total;
    return items.reduce((s, i) => s + (i.custom_price ?? i.price) * i.quantity, 0);
  }, [quote, items]);

  if (items.length === 0 && !err) {
    return (
      <StorefrontLayout>
        <div className="max-w-[1440px] mx-auto px-6 sm:px-10 py-24">
          <h1 className="font-display text-5xl font-medium tracking-tight">Your bag is empty.</h1>
          <p className="mt-4 text-base" style={{ color: "var(--ink-soft)" }}>Browse <Link to="/shop" className="underline">the collection</Link> first.</p>
        </div>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout>
      <section className="max-w-[1320px] mx-auto px-6 sm:px-10 py-12 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="label-mono mb-3">/ STEP 1 — DETAILS</div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Checkout.</h1>
          <form onSubmit={submit} className="mt-8 space-y-4 max-w-md">
            <Field label="Full name" testid="checkout-name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} required />
            <Field label="Email" type="email" testid="checkout-email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} required />
            {hasPhysical && (
              <div className="pt-2 space-y-3" data-testid="shipping-section">
                <div className="label-mono" style={{ color: "var(--accent)" }}>
                  / SHIPPING — REQUIRED FOR PHYSICAL ITEMS
                </div>
                <Field label="Address line 1" testid="ship-addr1"
                       value={shipping.address1} onChange={(v) => setShipping({ ...shipping, address1: v })} required />
                <Field label="Address line 2 (optional)" testid="ship-addr2"
                       value={shipping.address2} onChange={(v) => setShipping({ ...shipping, address2: v })} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="City" testid="ship-city"
                         value={shipping.city} onChange={(v) => setShipping({ ...shipping, city: v })} required />
                  <Field label="State / Province" testid="ship-state"
                         value={shipping.state_code} onChange={(v) => setShipping({ ...shipping, state_code: v.toUpperCase() })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="ZIP / Postal code" testid="ship-zip"
                         value={shipping.zip} onChange={(v) => setShipping({ ...shipping, zip: v })} required />
                  <label className="block">
                    <span className="label-mono">Country</span>
                    <select
                      data-testid="ship-country"
                      value={shipping.country_code}
                      onChange={(e) => setShipping({ ...shipping, country_code: e.target.value })}
                      className="mt-2 w-full px-4 py-3 rounded-lg outline-none"
                      style={{ background: "var(--paper)", border: "1px solid var(--rule)" }}
                    >
                      {["US", "CA", "GB", "AU", "DE", "FR", "IT", "ES", "NL", "SE", "NO", "DK", "IE", "NZ", "JP", "SG", "BR", "MX"].map((cc) => (
                        <option key={cc} value={cc}>{cc}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <Field label="Phone (optional, helps couriers)" testid="ship-phone"
                       value={shipping.phone} onChange={(v) => setShipping({ ...shipping, phone: v })} />
              </div>
            )}
            {err && <div data-testid="checkout-err" className="text-sm font-mono-ui" style={{ color: "var(--accent)" }}>{err}</div>}
            <button data-testid="place-order-btn" type="submit" disabled={busy || items.length === 0} className="btn-pill w-full justify-center">
              {busy ? "Redirecting…" : stripeReady ? `Pay $${totalDisplay.toFixed(2)} with Stripe →` : "Place order →"}
            </button>
            <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
              {stripeReady
                ? "Powered by Stripe. Apple Pay, Google Pay, Klarna, Cash App, Link and all major cards accepted."
                : "Stripe not configured. Orders are captured without payment until you add STRIPE_API_KEY."}
            </p>
          </form>
        </div>
        <div className="space-y-6">
          <div className="bx p-8 sm:p-10" style={{ background: "var(--paper-2)" }}>
            <div className="label-mono mb-4">/ STEP 2 — REVIEW</div>
            <div className="space-y-3">
              {items.map((i) => {
                const unit = i.custom_price != null ? i.custom_price : i.price;
                const tip = i.custom_price != null && i.custom_price > i.price ? i.custom_price - i.price : 0;
                return (
                  <div key={i.product_id} className="flex items-center justify-between rule-b pb-3">
                    <div>
                      <div className="font-display text-lg">{i.title}</div>
                      <div className="label-mono mt-1">
                        × {i.quantity}
                        {tip > 0 && <span className="ml-2" style={{ color: "var(--accent)" }}>+${tip.toFixed(2)} tip</span>}
                      </div>
                    </div>
                    <div className="font-mono-ui">${(unit * i.quantity).toFixed(2)}</div>
                  </div>
                );
              })}
            </div>

            {quote && (
              <div className="mt-4 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="label-mono">Subtotal</span>
                  <span className="font-mono-ui">${quote.subtotal.toFixed(2)}</span>
                </div>
                {quote.bundle_discount > 0 && (
                  <div className="flex justify-between" style={{ color: "var(--accent)" }}>
                    <span className="label-mono">Bundle {quote.bundle_pct}% off</span>
                    <span data-testid="checkout-bundle-discount" className="font-mono-ui">−${quote.bundle_discount.toFixed(2)}</span>
                  </div>
                )}
                {quote.coupon_discount > 0 && (
                  <div className="flex justify-between" style={{ color: "var(--accent)" }}>
                    <span className="label-mono">Coupon {quote.coupon?.code}</span>
                    <span data-testid="checkout-coupon-discount" className="font-mono-ui">−${quote.coupon_discount.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
            <div className="mt-6 pt-4 rule-t flex justify-between items-baseline">
              <span className="label-mono">Total</span>
              <span data-testid="checkout-total" className="font-mono-ui text-3xl">${totalDisplay.toFixed(2)}</span>
            </div>
          </div>

          {/* Order bump */}
          {bump && !bumpAdded && (
            <div data-testid="order-bump" className="bx p-6" style={{ background: "var(--ink)", color: "var(--paper)" }}>
              <div className="label-mono mb-2" style={{ color: "var(--accent-soft, #F2C6B4)" }}>/ ONE-CLICK COMPANION · UNLOCK 15% OFF</div>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-display text-xl mb-1 truncate">{bump.title}</div>
                  <div className="text-sm opacity-70 font-mono-ui">${bump.original_price.toFixed(2)}</div>
                  <p className="text-xs opacity-70 mt-2">
                    Add this companion from {bump.category} and we'll auto-apply <strong>MERIDIAN15</strong> — 15% off the whole order.
                  </p>
                </div>
                <button
                  data-testid="order-bump-add"
                  onClick={addBump}
                  className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium"
                  style={{ background: "var(--accent)", color: "var(--paper)" }}
                >
                  <Plus className="w-4 h-4 inline -mt-0.5" /> Add
                </button>
              </div>
            </div>
          )}
          {bumpAdded && (
            <div data-testid="order-bump-added" className="bx p-4 flex items-center gap-2 text-sm"
                 style={{ background: "var(--paper-2)", color: "var(--accent)" }}>
              <Check className="w-4 h-4" /> Companion added — 15% studio code is applied to the whole order.
            </div>
          )}
        </div>
      </section>
    </StorefrontLayout>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="label-mono">{label}</span>
      <input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full px-4 py-3 rounded-lg outline-none transition focus:ring-2"
        style={{ background: "var(--paper)", border: "1px solid var(--rule)" }}
      />
    </label>
  );
}
